/**
 * OrderPartsDialog — Dialog for ordering parts for a work order.
 *
 * Shows needed parts with quantity, allows supplier selection,
 * and creates POs via useOrderPartsForWorkOrder.
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useOrderPartsForWorkOrder } from '@/hooks/useWorkOrders';
import { useSuppliers, type SupplierListItem } from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';

interface NeededPart {
  id: string;
  partId: string | null;
  partName: string;
  partSku: string;
  quantity: number;
  unitPriceCents: number;
}

function catalogPartIds(parts: NeededPart[]): string[] {
  return parts.flatMap((p) => (p.partId ? [p.partId] : []));
}

interface OrderPartsDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
  neededParts: NeededPart[];
}

export function OrderPartsDialog({ open, onClose, workOrderId, neededParts }: OrderPartsDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const orderParts = useOrderPartsForWorkOrder();
  const { data: suppliersData } = useSuppliers({ isActive: true });

  const suppliers = useMemo<SupplierListItem[]>(() => {
    if (!suppliersData?.pages) return [];
    return suppliersData.pages.flatMap(page => page.data);
  }, [suppliersData]);

  const orderableIds = useMemo(() => catalogPartIds(neededParts), [neededParts]);

  // Track selected catalog parts and per-part supplier. Unmatched rows have
  // partId === null and must never enter the PO payload (#1110).
  const [selected, setSelected] = useState<Set<string>>(() => new Set(orderableIds));
  const [supplierOverrides, setSupplierOverrides] = useState<Record<string, string>>({});
  const [defaultSupplierId, setDefaultSupplierId] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(orderableIds));
    setSupplierOverrides({});
    setDefaultSupplierId('');
  }, [open, orderableIds]);

  const togglePart = (partId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId);
      else next.add(partId);
      return next;
    });
  };

  const allOrderableSelected = orderableIds.length > 0 && orderableIds.every((id) => selected.has(id));

  const handleOrder = async () => {
    const parts = neededParts
      .filter((p): p is NeededPart & { partId: string } => p.partId !== null && selected.has(p.partId))
      .map(p => ({
        partId: p.partId,
        quantity: p.quantity,
        ...(supplierOverrides[p.partId] ? { supplierId: supplierOverrides[p.partId] } : {}),
      }));

    if (parts.length === 0) return;

    try {
      await orderParts.mutateAsync({
        workOrderId,
        data: {
          parts,
          ...(defaultSupplierId ? { supplierId: defaultSupplierId } : {}),
        },
      });

      notify.success(
        t('partsOrders.orderCreated'),
        t('partsOrders.orderCreatedDesc', { count: parts.length }),
      );
      onClose();
    } catch {
      notify.error(tc('error'), '');
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('partsOrders.orderParts')}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('partsOrders.orderParts')}</DialogTitle>
      <DialogContent>
        {/* Default supplier selector */}
        <Box sx={{ mb: 2, mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('partsOrders.supplier')}</InputLabel>
            <Select
              value={defaultSupplierId}
              label={t('partsOrders.supplier')}
              onChange={(e) => setDefaultSupplierId(e.target.value)}
            >
              <MenuItem value="">
                <Chip label={t('partsOrders.autoSelectSupplier')} size="small" variant="outlined" />
              </MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allOrderableSelected}
                    indeterminate={selected.size > 0 && !allOrderableSelected}
                    disabled={orderableIds.length === 0}
                    onChange={() => {
                      setSelected(allOrderableSelected ? new Set() : new Set(orderableIds));
                    }}
                  />
                </TableCell>
                <TableCell>{t('partsOrders.partName')}</TableCell>
                <TableCell align="right">{t('partsOrders.quantity')}</TableCell>
                <TableCell>{t('partsOrders.supplier')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {neededParts.map((part) => {
                const inCatalog = part.partId !== null;
                return (
                <TableRow key={part.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={part.partId !== null && selected.has(part.partId)}
                      disabled={!inCatalog}
                      onChange={() => {
                        if (part.partId) togglePart(part.partId);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{part.partName}</Typography>
                    <Typography variant="caption" color="text.secondary">{part.partSku}</Typography>
                    {!inCatalog && (
                      <Typography variant="caption" color="warning.main" display="block">
                        {t('partsOrders.addToCatalogFirst')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{part.quantity}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 150 }} disabled={!inCatalog}>
                      <Select
                        value={part.partId ? (supplierOverrides[part.partId] ?? '') : ''}
                        displayEmpty
                        disabled={!inCatalog}
                        onChange={(e) => {
                          if (!part.partId) return;
                          setSupplierOverrides(prev => ({
                            ...prev,
                            [part.partId as string]: e.target.value,
                          }));
                        }}
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary">
                            {t('partsOrders.autoSelectSupplier')}
                          </Typography>
                        </MenuItem>
                        {suppliers.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => void handleOrder()}
          disabled={orderParts.isPending || selected.size === 0}
        >
          {t('partsOrders.orderParts')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
